<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  normalizeTicimax,
  normalizeShopify,
  normalizeTrendyol,
} from '../helpers/orderHelpers';
import { fetchMarketplaceJson as fetchMarketplace } from 'dashboard/helpers/marketplaceApi';

const router = useRouter();
const route = useRoute();

const fetchMarketplaceJson = path =>
  fetchMarketplace(route.params.accountId, path);

const TR_MS = 3 * 60 * 60 * 1000;
const toStr = d => d.toISOString().slice(0, 10);
const todayTR = () => toStr(new Date(Date.now() + TR_MS));
const addDays = (s, n) => {
  const d = new Date(`${s}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return toStr(d);
};

const today = todayTR();
const yesterday = addDays(today, -1);

const loading = ref(true);
const recentLoading = ref(true);

// 4 kanalın verileri: curr + prev
const ch = ref({
  ticimax: { revenue: 0, orders: 0 },
  ticimaxPrev: { revenue: 0, orders: 0 },
  trendyolKoza: { revenue: 0, orders: 0 },
  trendyolKozaPrev: { revenue: 0, orders: 0 },
  shopify: { revenue: 0, orders: 0 },
  shopifyPrev: { revenue: 0, orders: 0 },
  trendyolModax: { revenue: 0, orders: 0 },
  trendyolModaxPrev: { revenue: 0, orders: 0 },
});

const recentOrders = ref([]);

// ── Load KPI data ────────────────────────────────────────────────────────
// Tum kanallarin gunluk ozeti artik tek bir Rails endpoint'inden (yerel DB'ye
// periyodik senkronize edilen marketplace_orders tablosu) geliyor — ayni
// sorgudan geldigi icin Genel Durum ve Raporlar artik her zaman tutarli.
async function loadData() {
  loading.value = true;
  const [curr, prev] = await Promise.allSettled([
    fetchMarketplaceJson(`reports/summary?start=${today}&end=${today}`),
    fetchMarketplaceJson(`reports/summary?start=${yesterday}&end=${yesterday}`),
  ]);

  const next = { ...ch.value };
  const pick = report => ({
    revenue: report.netRevenue || report.totalRevenue || 0,
    orders: report.orderCount || 0,
  });

  if (curr.status === 'fulfilled') {
    next.ticimax = pick(curr.value.ticimax);
    next.trendyolKoza = pick(curr.value.trendyol_koza);
    next.shopify = pick(curr.value.shopify);
    next.trendyolModax = pick(curr.value.trendyol_modax);
  }
  if (prev.status === 'fulfilled') {
    next.ticimaxPrev = pick(prev.value.ticimax);
    next.trendyolKozaPrev = pick(prev.value.trendyol_koza);
    next.shopifyPrev = pick(prev.value.shopify);
    next.trendyolModaxPrev = pick(prev.value.trendyol_modax);
  }

  ch.value = next;
  loading.value = false;
}

// ── Recent orders ─────────────────────────────────────────────────────────
const RECENT_CHANNEL_META = {
  ticimax: { label: 'Ticimax · Koza Butik', dotClass: 'bg-amber-400' },
  shopify: { label: 'Shopify · ModaXLarge', dotClass: 'bg-emerald-500' },
  trendyol_koza: { label: 'Trendyol · Koza Butik', dotClass: 'bg-orange-400' },
  trendyol_modax: {
    label: 'Trendyol · ModaXLarge',
    dotClass: 'bg-purple-500',
  },
};
const normalizeByChannel = (channel, order) => {
  if (channel === 'shopify') return normalizeShopify(order);
  if (channel === 'trendyol_koza' || channel === 'trendyol_modax')
    return normalizeTrendyol(order, channel);
  return normalizeTicimax(order);
};

async function fetchRecent() {
  recentLoading.value = true;
  try {
    const range = `start=${addDays(today, -3)}&end=${today}&limit=15`;
    const data = await fetchMarketplaceJson(`orders?${range}`);
    recentOrders.value = (data.orders || [])
      .map(item => {
        const n = normalizeByChannel(item.channel, item.order);
        return {
          id: n.orderNo,
          ...RECENT_CHANNEL_META[item.channel],
          customer: n.customerName || '—',
          amount: n.amount,
          status: n.statusLabel,
          date: n.date,
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 15);
  } catch {
    recentOrders.value = [];
  } finally {
    recentLoading.value = false;
  }
}

// ── Computed ──────────────────────────────────────────────────────────────
const totalRevenue = computed(
  () =>
    ch.value.ticimax.revenue +
    ch.value.trendyolKoza.revenue +
    ch.value.shopify.revenue +
    ch.value.trendyolModax.revenue
);
const totalOrders = computed(
  () =>
    ch.value.ticimax.orders +
    ch.value.trendyolKoza.orders +
    ch.value.shopify.orders +
    ch.value.trendyolModax.orders
);
const kozaTotal = computed(
  () => ch.value.ticimax.revenue + ch.value.trendyolKoza.revenue
);
const kozaOrders = computed(
  () => ch.value.ticimax.orders + ch.value.trendyolKoza.orders
);
const modaxTotal = computed(
  () => ch.value.shopify.revenue + ch.value.trendyolModax.revenue
);
const modaxOrders = computed(
  () => ch.value.shopify.orders + ch.value.trendyolModax.orders
);

const kozaPrevTotal = computed(
  () => ch.value.ticimaxPrev.revenue + ch.value.trendyolKozaPrev.revenue
);
const modaxPrevTotal = computed(
  () => ch.value.shopifyPrev.revenue + ch.value.trendyolModaxPrev.revenue
);
const totalPrev = computed(() => kozaPrevTotal.value + modaxPrevTotal.value);

function pct(curr, prev) {
  if (!prev) return null;
  const v = ((curr - prev) / prev) * 100;
  return { val: Math.abs(v).toFixed(1), up: v >= 0 };
}

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  }).format(n);
}

function goTo(name) {
  router.push({ name, params: { accountId: route.params.accountId } });
}

function refresh() {
  loadData();
  fetchRecent();
}

onMounted(() => {
  loadData();
  fetchRecent();
});
</script>

<template>
  <div
    class="flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div>
        <h1 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Genel Durum
        </h1>
        <p class="text-xs text-slate-400 mt-0.5">
          {{ today }} · Bugünkü performans
        </p>
      </div>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        @click="refresh"
      >
        <span
          class="i-lucide-refresh-cw size-3.5"
          :class="loading ? 'animate-spin' : ''"
        />
        Yenile
      </button>
    </div>

    <div class="flex-1 overflow-y-auto p-6 space-y-6">
      <!-- ── Özet KPI ──────────────────────────────────────────────────── -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <!-- Toplam Ciro -->
        <div
          class="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm col-span-2 md:col-span-1"
        >
          <div class="flex items-center justify-between mb-3">
            <span
              class="text-xs font-semibold text-slate-400 uppercase tracking-widest"
              >Toplam Ciro</span>
            <span class="i-lucide-circle-dollar-sign size-4 text-woot-400" />
          </div>
          <p class="text-2xl font-bold text-slate-900 dark:text-slate-100">
            <span
              v-if="loading"
              class="inline-block w-28 h-7 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
            />
            <span v-else>{{ fmt(totalRevenue) }}</span>
          </p>
          <div
            v-if="!loading && pct(totalRevenue, totalPrev)"
            class="mt-2 flex items-center gap-1 text-xs font-semibold"
            :class="
              pct(totalRevenue, totalPrev).up
                ? 'text-emerald-500'
                : 'text-red-500'
            "
          >
            <span
              :class="
                pct(totalRevenue, totalPrev).up
                  ? 'i-lucide-trending-up size-3.5'
                  : 'i-lucide-trending-down size-3.5'
              "
            />
            %{{ pct(totalRevenue, totalPrev).val }} dünden
          </div>
          <p v-else-if="!loading" class="mt-2 text-xs text-slate-400">
            Dün: {{ fmt(totalPrev) }}
          </p>
        </div>

        <!-- Toplam Sipariş -->
        <div
          class="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm"
        >
          <div class="flex items-center justify-between mb-3">
            <span
              class="text-xs font-semibold text-slate-400 uppercase tracking-widest"
              >Sipariş</span>
            <span class="i-lucide-package size-4 text-blue-400" />
          </div>
          <p class="text-2xl font-bold text-slate-900 dark:text-slate-100">
            <span
              v-if="loading"
              class="inline-block w-12 h-7 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
            />
            <span v-else>{{ totalOrders }}</span>
          </p>
          <p class="mt-2 text-xs text-slate-400">adet bugün (4 kanal)</p>
        </div>

        <!-- Koza Butik Toplam -->
        <div
          class="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm border-t-2 border-t-amber-400"
        >
          <div class="flex items-center justify-between mb-3">
            <span
              class="text-xs font-semibold text-amber-500 uppercase tracking-widest"
              >Koza Butik</span>
            <span class="text-xs text-slate-400">Ticimax + TY</span>
          </div>
          <p class="text-xl font-bold text-slate-900 dark:text-slate-100">
            <span
              v-if="loading"
              class="inline-block w-20 h-6 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
            />
            <span v-else>{{ fmt(kozaTotal) }}</span>
          </p>
          <p class="mt-1 text-xs text-slate-500">
            {{ loading ? '—' : `${kozaOrders} sipariş` }}
          </p>
          <div
            v-if="!loading && pct(kozaTotal, kozaPrevTotal)"
            class="mt-1 flex items-center gap-1 text-xs font-semibold"
            :class="
              pct(kozaTotal, kozaPrevTotal).up
                ? 'text-emerald-500'
                : 'text-red-500'
            "
          >
            <span
              :class="
                pct(kozaTotal, kozaPrevTotal).up
                  ? 'i-lucide-arrow-up size-3'
                  : 'i-lucide-arrow-down size-3'
              "
            />
            %{{ pct(kozaTotal, kozaPrevTotal).val }}
          </div>
        </div>

        <!-- ModaXLarge Toplam -->
        <div
          class="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm border-t-2 border-t-purple-500"
        >
          <div class="flex items-center justify-between mb-3">
            <span
              class="text-xs font-semibold text-purple-500 uppercase tracking-widest"
              >ModaXLarge</span>
            <span class="text-xs text-slate-400">Shopify + TY</span>
          </div>
          <p class="text-xl font-bold text-slate-900 dark:text-slate-100">
            <span
              v-if="loading"
              class="inline-block w-20 h-6 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
            />
            <span v-else>{{ fmt(modaxTotal) }}</span>
          </p>
          <p class="mt-1 text-xs text-slate-500">
            {{ loading ? '—' : `${modaxOrders} sipariş` }}
          </p>
          <div
            v-if="!loading && pct(modaxTotal, modaxPrevTotal)"
            class="mt-1 flex items-center gap-1 text-xs font-semibold"
            :class="
              pct(modaxTotal, modaxPrevTotal).up
                ? 'text-emerald-500'
                : 'text-red-500'
            "
          >
            <span
              :class="
                pct(modaxTotal, modaxPrevTotal).up
                  ? 'i-lucide-arrow-up size-3'
                  : 'i-lucide-arrow-down size-3'
              "
            />
            %{{ pct(modaxTotal, modaxPrevTotal).val }}
          </div>
        </div>
      </div>

      <!-- ── 4 Kanal Kartları ──────────────────────────────────────────── -->
      <div>
        <h2
          class="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3"
        >
          Kanal Bazlı Bugün
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <!-- Ticimax / Koza Butik -->
          <div
            class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            <div
              class="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800"
            >
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                <div>
                  <p
                    class="text-xs font-bold text-amber-700 dark:text-amber-300 leading-none"
                  >
                    Ticimax
                  </p>
                  <p class="text-xs text-amber-500 mt-0.5">
                    Koza Butik · kozabutik.com
                  </p>
                </div>
              </div>
            </div>
            <div class="px-4 py-4">
              <p
                v-if="loading"
                class="h-6 w-24 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
              />
              <template v-else>
                <p class="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {{ fmt(ch.ticimax.revenue) }}
                </p>
                <p class="text-xs text-slate-400 mt-1">
                  {{ ch.ticimax.orders }} sipariş
                </p>
                <div
                  v-if="pct(ch.ticimax.revenue, ch.ticimaxPrev.revenue)"
                  class="mt-1.5 flex items-center gap-1 text-xs font-semibold"
                  :class="
                    pct(ch.ticimax.revenue, ch.ticimaxPrev.revenue).up
                      ? 'text-emerald-500'
                      : 'text-red-500'
                  "
                >
                  <span
                    :class="
                      pct(ch.ticimax.revenue, ch.ticimaxPrev.revenue).up
                        ? 'i-lucide-arrow-up size-3'
                        : 'i-lucide-arrow-down size-3'
                    "
                  />
                  %{{ pct(ch.ticimax.revenue, ch.ticimaxPrev.revenue).val }}
                  dünden
                </div>
              </template>
            </div>
          </div>

          <!-- Trendyol / Koza Butik -->
          <div
            class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            <div
              class="px-4 py-3 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-100 dark:border-orange-800"
            >
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-orange-400 shrink-0" />
                <div>
                  <p
                    class="text-xs font-bold text-orange-700 dark:text-orange-300 leading-none"
                  >
                    Trendyol
                  </p>
                  <p class="text-xs text-orange-500 mt-0.5">
                    Koza Butik · #413042
                  </p>
                </div>
              </div>
            </div>
            <div class="px-4 py-4">
              <p
                v-if="loading"
                class="h-6 w-24 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
              />
              <template v-else>
                <p class="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {{ fmt(ch.trendyolKoza.revenue) }}
                </p>
                <p class="text-xs text-slate-400 mt-1">
                  {{ ch.trendyolKoza.orders }} sipariş
                </p>
                <div
                  v-if="
                    pct(ch.trendyolKoza.revenue, ch.trendyolKozaPrev.revenue)
                  "
                  class="mt-1.5 flex items-center gap-1 text-xs font-semibold"
                  :class="
                    pct(ch.trendyolKoza.revenue, ch.trendyolKozaPrev.revenue).up
                      ? 'text-emerald-500'
                      : 'text-red-500'
                  "
                >
                  <span
                    :class="
                      pct(ch.trendyolKoza.revenue, ch.trendyolKozaPrev.revenue)
                        .up
                        ? 'i-lucide-arrow-up size-3'
                        : 'i-lucide-arrow-down size-3'
                    "
                  />
                  %{{
                    pct(ch.trendyolKoza.revenue, ch.trendyolKozaPrev.revenue)
                      .val
                  }}
                  dünden
                </div>
              </template>
            </div>
          </div>

          <!-- Shopify / ModaXLarge -->
          <div
            class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            <div
              class="px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-100 dark:border-emerald-800"
            >
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                <div>
                  <p
                    class="text-xs font-bold text-emerald-700 dark:text-emerald-300 leading-none"
                  >
                    Shopify
                  </p>
                  <p class="text-xs text-emerald-500 mt-0.5">
                    ModaXLarge · modaxlarge.com
                  </p>
                </div>
              </div>
            </div>
            <div class="px-4 py-4">
              <p
                v-if="loading"
                class="h-6 w-24 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
              />
              <template v-else>
                <p class="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {{ fmt(ch.shopify.revenue) }}
                </p>
                <p class="text-xs text-slate-400 mt-1">
                  {{ ch.shopify.orders }} sipariş
                </p>
                <div
                  v-if="pct(ch.shopify.revenue, ch.shopifyPrev.revenue)"
                  class="mt-1.5 flex items-center gap-1 text-xs font-semibold"
                  :class="
                    pct(ch.shopify.revenue, ch.shopifyPrev.revenue).up
                      ? 'text-emerald-500'
                      : 'text-red-500'
                  "
                >
                  <span
                    :class="
                      pct(ch.shopify.revenue, ch.shopifyPrev.revenue).up
                        ? 'i-lucide-arrow-up size-3'
                        : 'i-lucide-arrow-down size-3'
                    "
                  />
                  %{{ pct(ch.shopify.revenue, ch.shopifyPrev.revenue).val }}
                  dünden
                </div>
              </template>
            </div>
          </div>

          <!-- Trendyol / ModaXLarge -->
          <div
            class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            <div
              class="px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-100 dark:border-purple-800"
            >
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
                <div>
                  <p
                    class="text-xs font-bold text-purple-700 dark:text-purple-300 leading-none"
                  >
                    Trendyol
                  </p>
                  <p class="text-xs text-purple-500 mt-0.5">
                    ModaXLarge · #993542
                  </p>
                </div>
              </div>
            </div>
            <div class="px-4 py-4">
              <p
                v-if="loading"
                class="h-6 w-24 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
              />
              <template v-else>
                <p class="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {{ fmt(ch.trendyolModax.revenue) }}
                </p>
                <p class="text-xs text-slate-400 mt-1">
                  {{ ch.trendyolModax.orders }} sipariş
                </p>
                <div
                  v-if="
                    pct(ch.trendyolModax.revenue, ch.trendyolModaxPrev.revenue)
                  "
                  class="mt-1.5 flex items-center gap-1 text-xs font-semibold"
                  :class="
                    pct(ch.trendyolModax.revenue, ch.trendyolModaxPrev.revenue)
                      .up
                      ? 'text-emerald-500'
                      : 'text-red-500'
                  "
                >
                  <span
                    :class="
                      pct(
                        ch.trendyolModax.revenue,
                        ch.trendyolModaxPrev.revenue
                      ).up
                        ? 'i-lucide-arrow-up size-3'
                        : 'i-lucide-arrow-down size-3'
                    "
                  />
                  %{{
                    pct(ch.trendyolModax.revenue, ch.trendyolModaxPrev.revenue)
                      .val
                  }}
                  dünden
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Son Siparişler ────────────────────────────────────────────── -->
      <div
        class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden"
      >
        <div
          class="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between"
        >
          <h3
            class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest"
          >
            Son Siparişler
          </h3>
          <button
            class="text-xs text-woot-500 hover:text-woot-600 font-medium transition-colors"
            @click="goTo('orders_index')"
          >
            Tümünü Gör →
          </button>
        </div>

        <div
          v-if="recentLoading"
          class="divide-y divide-slate-100 dark:divide-slate-700"
        >
          <div
            v-for="i in 6"
            :key="i"
            class="px-5 py-3 flex items-center gap-3"
          >
            <div
              class="w-2 h-2 rounded-full bg-slate-100 dark:bg-slate-700 animate-pulse shrink-0"
            />
            <div
              class="w-32 h-3 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
            />
            <div
              class="flex-1 h-3 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
            />
            <div
              class="w-20 h-3 bg-slate-100 dark:bg-slate-700 rounded animate-pulse"
            />
          </div>
        </div>

        <div
          v-else-if="recentOrders.length === 0"
          class="px-5 py-10 text-center text-sm text-slate-400"
        >
          <span
            class="i-lucide-package-open size-8 mb-2 block mx-auto opacity-30"
          />
          Son 3 günde sipariş bulunamadı
        </div>

        <div v-else class="divide-y divide-slate-100 dark:divide-slate-700">
          <div
            v-for="order in recentOrders"
            :key="`${order.label}-${order.id}`"
            class="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
            @click="goTo('orders_index')"
          >
            <span
              class="shrink-0 w-2 h-2 rounded-full"
              :class="order.dotClass"
            />
            <span class="text-xs text-slate-400 shrink-0 w-36 truncate">{{
              order.label
            }}</span>
            <span
              class="text-xs font-mono text-slate-400 shrink-0 w-14 truncate"
              >{{ order.id }}</span>
            <span
              class="flex-1 text-xs text-slate-700 dark:text-slate-300 truncate min-w-0"
              >{{ order.customer }}</span>
            <span
              class="text-xs font-bold text-slate-900 dark:text-slate-100 shrink-0"
              >{{ fmt(order.amount) }}</span>
            <span class="text-xs text-slate-400 shrink-0 hidden sm:block">{{
              order.date
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
